import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AlertsType } from '@rappi/ui/alerts';
import { switchMap } from 'rxjs/operators';

import { AlertsService } from '../../../../../services/alerts/alerts.service';
import { StoreAuxService } from '../../../../../services/storeAux/store-aux.service';
import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { CompanyDocumentInput, CompanyDocumentRecord } from '../../../../models/makers-finances-mgr.models';
import {
  PoDocumentPreviewDialogComponent,
} from '../../../purchase-order-detail/po-document-preview-dialog/po-document-preview-dialog.component';
import {
  FH_COMPANY_LABEL,
  FH_FILE_ERROR,
  FH_FILE_FALLBACK,
  FH_FILE_MESSAGE,
  FH_FILE_MIME_MAP,
} from '../../company-form.constants';
import {
  CompanyDocumentTypeConfig,
  CompanyFieldDescriptor,
  CompanyFormSection,
  FhUploadedFile,
} from '../../company-form.types';

interface RejectedFile {
  name: string;
  reason: string;
}

// The Tax document / Contract cards: a full-width dashed dropzone plus the list of uploaded files.
//
// Extends the single-file section-po-document pattern to N files. `accept` and `max_size_mb` come
// from the field config with constants as fallback, mirroring the repo's "config with a constant
// fallback" convention. There is no batch upload endpoint, so files fan out over POST
// /files/upload; a partial failure is surfaced per file rather than failing the whole drop.
//
// Document lifecycle for an EXISTING company (companyId set) follows
// docs/documents/COMPANY_DOCUMENTS_API.md in makers-finances-mgr: every add/remove is persisted
// immediately against POST/DELETE /companies/:id/documents rather than deferred to the form's own
// Save — CompanyFormComponent excludes `documents` from PUT /companies/:id for exactly this reason
// (see company-form.payload.ts). While the company doesn't exist yet (companyId null), files stay
// local-only and are collected into POST /companies' inline `documents` array instead.
@Component({
  selector: 'app-company-files-card',
  templateUrl: './company-files-card.component.html',
  styleUrls: ['./company-files-card.component.scss'],
})
export class CompanyFilesCardComponent implements OnChanges {
  /* istanbul ignore next -- decorator metadata */
  @Input() section: CompanyFormSection;
  /* istanbul ignore next -- decorator metadata */
  @Input() form: FormGroup;
  /* istanbul ignore next -- decorator metadata */
  @Input() submitted = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() required = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() companyId: number | null = null;
  /* istanbul ignore next -- decorator metadata */
  @Input() documentConfig: CompanyDocumentTypeConfig | null = null;
  /* istanbul ignore next -- decorator metadata */
  @Input() strictMode = false;

  readonly LABELS = FH_COMPANY_LABEL;
  readonly TYPE_NOT_ALLOWED_MESSAGE = FH_FILE_MESSAGE[FH_FILE_ERROR.TYPE_NOT_ALLOWED];

  isDragging = false;
  uploading = false;
  deleting = false;
  rejected: RejectedFile[] = [];

  private loadedForCompanyId: number | null = null;

  constructor(
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alerts: AlertsService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && this.companyId && this.companyId !== this.loadedForCompanyId) {
      this.loadedForCompanyId = this.companyId;
      this.loadDocuments(this.companyId);
    }
  }

  get field(): CompanyFieldDescriptor | null {
    return this.section?.fields?.[0] ?? null;
  }

  get files(): FhUploadedFile[] {
    return (this.form.get(this.field?.controlName ?? '')?.value as FhUploadedFile[]) ?? [];
  }

  get acceptedExtensions(): string[] {
    const configured = this.field?.accept ?? [];
    return configured.length ? configured : FH_FILE_FALLBACK.ACCEPT;
  }

  get acceptAttr(): string {
    return this.acceptedExtensions.map(extension => `.${extension}`).join(',');
  }

  get maxSizeMb(): number {
    return this.field?.maxSizeMb ?? FH_FILE_FALLBACK.MAX_SIZE_MB;
  }

  get allowMultiple(): boolean {
    return !!this.field?.multiple;
  }

  get showRequiredError(): boolean {
    return this.submitted && this.required && !this.files.length;
  }

  // False only in strict mode (CMS has `company_documents.types`) when this field's payload was
  // dropped from the CMS list — see docs/documents/COMPANY_DOCUMENTS_ARCHITECTURE.md §12.
  get isTypeAllowed(): boolean {
    return !this.strictMode || !!this.documentConfig;
  }

  get busy(): boolean {
    return this.uploading || this.deleting;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(): void {
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    this.addFiles(event.dataTransfer?.files ?? null);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    // Reset so selecting the same file again still fires a change event.
    input.value = '';
  }

  addFiles(list: FileList | null): void {
    if (!list || !list.length) {
      return;
    }
    if (!this.isTypeAllowed) {
      this.alerts.openAlerts(FH_FILE_MESSAGE[FH_FILE_ERROR.TYPE_NOT_ALLOWED], AlertsType.error);
      return;
    }

    const incoming = Array.from(list);
    const accepted: File[] = [];
    this.rejected = [];

    incoming.forEach(file => {
      const reason = this.validate(file);
      if (reason) {
        this.rejected.push({ name: file.name, reason });
      } else {
        accepted.push(file);
      }
    });

    if (this.rejected.length) {
      // One aggregated alert rather than one toast per rejected file.
      this.alerts.openAlerts(this.rejected[0].reason, AlertsType.error);
    }
    if (!accepted.length) {
      return;
    }

    const toUpload = this.allowMultiple ? accepted : accepted.slice(0, 1);

    // Client-side pre-check mirroring the backend's "current + new <= max" rule (see
    // docs/documents/COMPANY_DOCUMENTS_API.md §Validaciones) — avoids an upload round trip the
    // add would just reject anyway. The backend still enforces this authoritatively.
    if (this.wouldExceedMax(toUpload.length)) {
      this.alerts.openAlerts(FH_FILE_MESSAGE[FH_FILE_ERROR.MAX_EXCEEDED], AlertsType.error);
      return;
    }

    this.uploading = true;
    this.financesService.uploadFiles(toUpload, this.storeAux.country).subscribe({
      next: uploaded => {
        const now = new Date().toISOString();
        const added: FhUploadedFile[] = uploaded.map((data, index) => ({
          key: data.key,
          name: toUpload[index].name,
          uploadedAt: now,
        }));

        if (this.companyId) {
          // The company already exists: persist immediately via POST /companies/:id/documents
          // rather than waiting for the form's own Save (see the class-level doc comment).
          this.persistAdded(this.companyId, added);
          return;
        }

        this.uploading = false;
        const next = this.allowMultiple ? [...this.files, ...added] : added;
        this.setValue(next);
        this.alerts.openAlerts(FH_FILE_MESSAGE['UPLOAD_COMPLETE'], AlertsType.success);
      },
      error: () => {
        this.uploading = false;
        this.alerts.openAlerts(FH_FILE_MESSAGE[FH_FILE_ERROR.UPLOAD_FAILED], AlertsType.error);
      },
    });
  }

  removeFile(index: number): void {
    const file = this.files[index];
    if (!file) {
      return;
    }

    if (this.companyId && file.id) {
      this.removePersisted(this.companyId, file.id, index);
      return;
    }

    const next = [...this.files];
    next.splice(index, 1);
    this.setValue(next);
  }

  openPreview(file: FhUploadedFile): void {
    this.financesService.previewFile(file.key, this.storeAux.country).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        this.dialog.open(PoDocumentPreviewDialogComponent, {
          width: '95vw',
          maxWidth: '95vw',
          height: '90vh',
          panelClass: 'podp-panel',
          data: { url, name: file.name, mimeType: blob.type },
        });
      },
      error: () => {
        this.alerts.openAlerts(FH_FILE_MESSAGE[FH_FILE_ERROR.UPLOAD_FAILED], AlertsType.error);
      },
    });
  }

  trackByKey(_index: number, file: FhUploadedFile): string {
    return file.key;
  }

  // `file.type` is empty or wrong in some browsers, so the extension is the primary check and the
  // mime map is a secondary accept.
  private validate(file: File): string | null {
    const extension = (file.name.split('.').pop() ?? '').toLowerCase();
    const allowedExtensions = this.acceptedExtensions.map(value => value.toLowerCase());
    const allowedMimes = allowedExtensions
      .reduce<string[]>((acc, ext) => acc.concat(FH_FILE_MIME_MAP[ext] ?? []), []);

    const extensionOk = allowedExtensions.indexOf(extension) !== -1;
    const mimeOk = !!file.type && allowedMimes.indexOf(file.type) !== -1;
    if (!extensionOk && !mimeOk) {
      return FH_FILE_MESSAGE[FH_FILE_ERROR.INVALID_FORMAT];
    }
    if (file.size > this.maxSizeMb * 1024 * 1024) {
      return FH_FILE_MESSAGE[FH_FILE_ERROR.SIZE_EXCEEDED];
    }
    return null;
  }

  // GET /companies/:id/documents on first appearance of a real companyId (edit mode, or a create
  // flow that just got its first id from Save) — prefills this field's files, ids included, so
  // later removals know which document to DELETE.
  private loadDocuments(companyId: number): void {
    this.financesService.getCompanyDocuments(companyId).subscribe({
      next: res => this.applyPersistedDocuments(res.data),
      error: () => this.alerts.openAlerts(FH_FILE_MESSAGE[FH_FILE_ERROR.LOAD_FAILED], AlertsType.error),
    });
  }

  // POST /companies/:id/documents, then re-fetch the list: the add response carries no `data`, so
  // the newly assigned ids (needed later to DELETE) are only known once the list is re-read.
  private persistAdded(companyId: number, added: FhUploadedFile[]): void {
    const fieldName = this.field?.payload ?? '';
    const payload: CompanyDocumentInput[] = added.map(file => ({
      field_name: fieldName,
      document_key: file.key,
      document_name: file.name,
    }));

    this.financesService.addCompanyDocuments(companyId, payload).pipe(
      switchMap(() => this.financesService.getCompanyDocuments(companyId)),
    ).subscribe({
      next: res => {
        this.uploading = false;
        this.applyPersistedDocuments(res.data);
        this.alerts.openAlerts(FH_FILE_MESSAGE['UPLOAD_COMPLETE'], AlertsType.success);
      },
      error: error => {
        this.uploading = false;
        this.alerts.openAlerts(
          this.backendErrorMessage(error, FH_FILE_MESSAGE[FH_FILE_ERROR.ADD_FAILED]),
          AlertsType.error,
        );
      },
    });
  }

  // DELETE /companies/:id/documents/:docId — soft-delete. Guarded client-side by the same `min`
  // rule the backend enforces (docs/documents/COMPANY_DOCUMENTS_API.md §4), so an obviously-doomed
  // request never leaves the browser.
  private removePersisted(companyId: number, docId: number, index: number): void {
    if (this.wouldViolateMin()) {
      this.alerts.openAlerts(FH_FILE_MESSAGE[FH_FILE_ERROR.MIN_VIOLATION], AlertsType.error);
      return;
    }

    this.deleting = true;
    this.financesService.deleteCompanyDocument(companyId, docId).subscribe({
      next: () => {
        this.deleting = false;
        const next = [...this.files];
        next.splice(index, 1);
        this.setValue(next);
        this.alerts.openAlerts(FH_FILE_MESSAGE['DOCUMENT_REMOVED'], AlertsType.success);
      },
      error: error => {
        this.deleting = false;
        this.alerts.openAlerts(
          this.backendErrorMessage(error, FH_FILE_MESSAGE[FH_FILE_ERROR.REMOVE_FAILED]),
          AlertsType.error,
        );
      },
    });
  }

  // `max === 0` (or unset) means "no limit" per the CMS contract.
  private wouldExceedMax(additionalCount: number): boolean {
    const max = this.documentConfig?.max;
    if (!max) {
      return false;
    }
    return this.files.length + additionalCount > max;
  }

  // `min === 0` (or unset) means "no minimum" per the CMS contract.
  private wouldViolateMin(): boolean {
    const min = this.documentConfig?.min;
    if (!min) {
      return false;
    }
    return this.files.length - 1 < min;
  }

  // The backend crafts specific, user-facing copy for these errors (unknown document type, max/min
  // violations) — surfacing it directly is the documented behaviour (see "Mostrar mensajes de
  // error específicos del backend" in COMPANY_DOCUMENTS_QUICK_REFERENCE.md) with our own constant
  // as a fallback for anything that isn't the expected `{ message }` shape (network errors, etc.).
  private backendErrorMessage(error: unknown, fallback: string): string {
    const body = (error as { error?: { message?: string } } | null)?.error;
    return typeof body?.message === 'string' && body.message.trim() ? body.message : fallback;
  }

  private applyPersistedDocuments(records: CompanyDocumentRecord[]): void {
    const fieldName = this.field?.payload;
    const mine: FhUploadedFile[] = records
      .filter(doc => doc.field_name === fieldName)
      .map(doc => ({ id: doc.id, key: doc.document_key, name: doc.document_name, uploadedAt: doc.uploaded_at }));
    this.setValue(mine);
  }

  private setValue(files: FhUploadedFile[]): void {
    const control = this.form.get(this.field?.controlName ?? '');
    control?.setValue(files);
    control?.markAsTouched();
  }
}
